import axios from "axios";
import { Auth } from "aws-amplify";
import { endpoints } from "../config/endpoints";

// Get the todos endpoint from the configuration
const TODOS_API_ENDPOINT = endpoints.api.todos;

/**
 * Get the current authenticated user's ID from Amplify
 * @returns {Promise<string|null>} The user ID or null if not available
 */
const getCurrentUserId = async () => {
  try {
    const currentUser = await Auth.currentAuthenticatedUser();

    // For Cognito users, use the sub attribute
    if (currentUser.attributes && currentUser.attributes.sub) {
      console.log("Using Cognito sub as user ID:", currentUser.attributes.sub);
      return currentUser.attributes.sub;
    }
    // For federated identities (like Google), check the idToken
    else if (currentUser.signInUserSession?.idToken?.payload?.sub) {
      console.log(
        "Using idToken sub as user ID:",
        currentUser.signInUserSession.idToken.payload.sub
      );
      return currentUser.signInUserSession.idToken.payload.sub;
    }
    // Fallback to username if available
    else if (currentUser.username) {
      console.log("Using username as user ID:", currentUser.username);
      return currentUser.username;
    }

    console.error("Could not determine user ID from:", currentUser);
    return null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

/**
 * Fetch todos for the current user
 * @param {string} todoCategory - The category of todos to fetch (default: "general")
 * @returns {Promise<Array>} - A promise that resolves to an array of todos
 */
export const fetchUserTodos = async (todoCategory = "general") => {
  try {
    const userId = await getCurrentUserId();

    if (!userId) {
      console.error("No user ID available from Amplify");
      return [];
    }

    console.log(
      `Fetching todos for user ${userId} in category ${todoCategory}`
    );

    // Get the current session token from Amplify Auth
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();

    if (!idToken) {
      console.error("No authentication token available");
      return [];
    }

    // Using the middleware Lambda function endpoint that handles CORS properly
    console.log(`Using todos endpoint: ${TODOS_API_ENDPOINT}`);

    // Define headers similar to how threadUtils.js does it
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
      Origin: window.location.origin,
    };

    console.log("Making request to todos endpoint with headers:", headers);

    // Important: Remove withCredentials: true to match threadUtils.js approach
    const response = await axios.post(
      TODOS_API_ENDPOINT,
      {
        user_id: userId,
        todo_category: todoCategory,
      },
      {
        headers,
      }
    );

    if (response.data && response.data.todos) {
      console.log(
        `Successfully fetched ${response.data.todos.length} todos for user ${userId}`
      );
      return response.data.todos;
    } else {
      console.log(
        "No todos found or unexpected response format:",
        response.data
      );
      return [];
    }
  } catch (error) {
    console.error("Error fetching todos:", error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error("No response received:", error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
};
